<template>
  <SfDropdown
    :is-open="isScopedDropdownOpen"
    :dropdown-id="dropdownId"
    @dropdown-clicked="handleToggleDropdown"
    @dropdown-closed="closeDropdown"
  >
    <template #trigger>
      <div class="tw-flex tw-items-center">
        <div
          class="
            tw-flex-1
            tw-min-w-0
            tw-whitespace-nowrap
            tw-text-ellipsis
            tw-overflow-hidden
          "
          :class="triggerDisplayClass"
        >
          {{ triggerDisplayValue }}
        </div>
        <span
          v-if="selectedValue"
          class="
            fa fa-remove
            tw-cursor-pointer
            sf-search__icon sf-search__icon--right
            tw-ml-2
          "
          data-testid="sf-search-clear"
          @click.prevent.stop="clearSelected"
        />
      </div>
    </template>

    <template #menu>
      <div class="tw-p-2 tw-overflow-hidden">
        <div class="tw-mb-2">
          <SfSearch
            v-model="searchValue"
            :show-search-icon="true"
            :show-clear-icon="true"
            :show-result-navigation="false"
            :placeholder="searchPlaceholder"
            :bottom-margin="true"
            :focused-at="focusedAt"
            @clear="clearSearch"
          />
        </div>
        <div class="tw-overflow-y-auto tw-max-h-[350px]">
          <SfDropdownItem
            v-for="item in filteredOptions"
            :key="item.value"
            :is-wrapped="false"
            :has-border="false"
            :is-rounded="true"
            :has-checkmark="isSelected(item.value)"
            :class="{
              'tw-font-medium tw-bg-sf-primary-subtle': isSelected(item.value),
            }"
            class="tw-leading-3"
            @click="handleItemClick(item.value)"
          >
            <span
              v-if="item.icon"
              :class="`fa-${item.icon} tw-text-${item.iconColor}`"
              class="fa tw-mr-3"
            />
            <span>{{ item.label }}</span>
          </SfDropdownItem>
        </div>
      </div>
    </template>
  </SfDropdown>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useVModel } from '@vueuse/core'
import useI18n from '@app/shared/vue/composables/use-I18n'
import { useSearchable } from '@app/shared/vue/composables/use-searchable'
import { useDropdown } from '@app/shared/pinia/dropdown'
import SfDropdown from '@app/shared/vue/components/sf-dropdown/sf-dropdown.vue'
import SfDropdownItem from '@app/shared/vue/components/sf-dropdown/sf-dropdown-item.vue'
import SfSearch from '@app/shared/vue/components/sf-search/search-input.vue'

interface Option {
  label: string
  value: string | number
  icon?: string
  iconColor?: string
}

interface SearchableDropdownProps {
  modelValue: string | number | null
  options: Option[]
  dropdownId?: string
  isDisabled?: boolean
  placeholder?: string | null
  searchPlaceholder?: string | null
}

const props = withDefaults(defineProps<SearchableDropdownProps>(), {
  dropdownId: 'searchableDropdown',
  placeholder: null,
  searchPlaceholder: null,
})

const I18n = useI18n()
const dropdownStore = useDropdown()

const emit =
  defineEmits<{
    'update:modelValue': [value: string | number | null]
    opened: []
    closed: []
  }>()

const selectedValue = useVModel(props, 'modelValue', emit, {
  deep: true,
  passive: true,
})
const { currentDropdownId } = storeToRefs(dropdownStore)
const focusedAt = ref<number | undefined>(undefined)

const selectedOption = computed(() => {
  if (!selectedValue.value) {
    return null
  }

  return props.options.find((option) => option.value === selectedValue.value)
})

const triggerDisplayValue = computed(() => {
  if (selectedOption.value) {
    return selectedOption.value.label
  } else {
    return props.placeholder || I18n.t('common.select')
  }
})

const flattenedOptions = computed(() => {
  return props.options.flat()
})

const { searchValue, results: filteredOptions } = useSearchable<Option, Option>(
  flattenedOptions,
  {
    prepareItem: (item) => item,
    fuzzySortOptions: { keys: ['label'] },
  }
)

const isSelected = computed(() => {
  return (value: string | number) => selectedValue.value === value
})

const isScopedDropdownOpen = computed(() => {
  return currentDropdownId.value === props.dropdownId
})

const triggerDisplayClass = computed(() => {
  return {
    'tw-text-sf-gray-darker': selectedOption.value,
    'tw-text-sf-gray-light': !selectedOption.value,
    'tw-text-sf-gray-lighter': props.isDisabled,
    'tw-italic': !selectedOption.value,
  }
})

function handleToggleDropdown() {
  if (isScopedDropdownOpen.value) {
    emit('closed')
  } else {
    emit('opened')
  }

  dropdownStore.toggleDropdown(props.dropdownId)
  focusSearch()
}

function closeDropdown() {
  dropdownStore.closeDropdown()
  emit('closed')
}

function clearSearch(): void {
  searchValue.value = ''
}

function clearSelected(): void {
  selectedValue.value = null
}

function focusSearch(): void {
  focusedAt.value = Date.now()
}

function handleItemClick(value: string | number): void {
  selectedValue.value = value
  closeDropdown()
}
</script>
